// src/contexts/AuthContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  TelcoFair · Auth Context
//  Provides authentication state and functions to the entire app
//  Super Admin accounts only
// ─────────────────────────────────────────────────────────────────────────────

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { auth, onAuthStateChanged } from '../config/firebase';
import authService from '../services/api/authService';
import { adminAccessMessage, friendlyError } from '../utils/userMessages';

const AuthContext = createContext();

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    // Allowed roles for admin portal access
    const ALLOWED_ROLES = ['super_admin', 'superadmin'];

    // Check if user has allowed role
    const normaliseRole = (role) => String(role || '').toLowerCase().replace(/[\s-]+/g, '_');

    const hasAllowedRole = (role) => {
        return ALLOWED_ROLES.includes(normaliseRole(role));
    };


    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            setLoading(true);
            
            if (firebaseUser) {
                try {
                    setUser(firebaseUser);
                    
                    // Admin portal access must be backed by an explicit Firestore
                    // administrator profile. Missing/timeout data is denied rather
                    // than silently elevated to admin.
                    const { db, doc, getDoc } = await import('../config/firebase');
                    const userDoc = await Promise.race([
                        getDoc(doc(db, 'users', firebaseUser.uid)),
                        new Promise((_, reject) =>
                            setTimeout(() => reject(new Error('Administrator profile lookup timed out')), 5000)
                        )
                    ]);
                    if (!userDoc || !userDoc.exists()) {
                        const { signOut } = await import('firebase/auth');
                        await signOut(auth);
                        throw new Error('Administrator profile not found.');
                    }
                    const data = userDoc.data() || {};
                    const role = normaliseRole(data.role || data.userRole || '');
                    const employeeMeetingRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/employee-room/');
                    const employeeMeetingAccess = employeeMeetingRoute && role === 'platform_employee' && data.isActive !== false && data.isBlocked !== true;
                    if ((!hasAllowedRole(role) && !employeeMeetingAccess) || data.isActive === false || data.isBlocked === true) {
                        const { signOut } = await import('firebase/auth');
                        await signOut(auth);
                        throw new Error(adminAccessMessage);
                    }
                    setUserData({
                        ...data,
                        userId: firebaseUser.uid,
                        email: firebaseUser.email,
                    });
                } catch (error) {
                    console.error('Error loading user data:', error);
                    setUser(null);
                    setUserData(null);
                }
            } else {
                setUser(null);
                setUserData(null);
            }
            
            setLoading(false);
        });

        return () => unsubscribe();
    }, [navigate]);

    const loginWithEmail = async (email, password, rememberMe) => {
        try {
            const result = await authService.loginWithEmail(email, password, rememberMe);
            
            // Get user data from result
            const userData = result.userData || {};
            const role = normaliseRole(userData.role || userData.userRole || '');
            
            if (!hasAllowedRole(role) || userData.isActive === false || userData.isBlocked === true) {
                await authService.logout().catch(() => {});
                throw new Error(adminAccessMessage);
            }
            
            setUser(result.user);
            setUserData({ ...userData, userId: result.user?.uid || userData.userId, email: result.user?.email || userData.email });
            toast.success('Welcome back!');
            
            return { 
                user: result.user, 
                userData: userData,
                role: role
            };
        } catch (error) {
            const message = error?.message === adminAccessMessage ? adminAccessMessage : friendlyError(error, 'Unable to sign in. Please check your credentials and try again.');
            toast.error(message);
            throw new Error(message);
        }
    };

    const loginWithPhone = async () => {
        throw new Error('Phone/password login is not configured for the Super Admin Portal. Use administrator email login.');
    };

    const sendOTP = async () => {
        throw new Error('OTP login is not configured for the Super Admin Portal.');
    };

    const verifyOTP = async () => {
        throw new Error('OTP login is not configured for the Super Admin Portal.');
    };

    const logout = async () => {
        try {
            await authService.logout();
            setUser(null);
            setUserData(null);
            toast.success('Logged out successfully');
            navigate('/login');
        } catch (error) {
            toast.error('Failed to logout');
            throw error;
        }
    };

    const resetPassword = async (email) => {
        try {
            const result = await authService.resetPassword(email);
            toast.success(result.message);
            return result;
        } catch (error) {
            const message = friendlyError(error, 'Unable to request a password reset.');
            toast.error(message);
            throw new Error(message);
        }
    };

    const value = {
        user,
        userData,
        loading,
        loginWithEmail,
        loginWithPhone,
        sendOTP,
        verifyOTP,
        logout,
        resetPassword,
        isAuthenticated: !!user,
        isAdmin: hasAllowedRole(userData?.role || userData?.userRole || ''),
        // Helper to check if user has specific role
        hasRole: (role) => normaliseRole(userData?.role || userData?.userRole || '') === normaliseRole(role),
            isCompanyAdmin: false,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;